import { useRef, useCallback, useState, useEffect } from 'react';
import { useEditorActions } from '../shell/editorActionsContext';
import { useEditorState, useEditor, getRootLayers, getCompositionProps, formatTimecode, UPDATE_PROPERTY, ADD_EFFECT, ADD_KEYFRAME, REMOVE_KEYFRAME, REMOVE_EFFECT, snapToFrame } from '@/lib/kinema';
import type { BaseLayerData, Effect } from '@/lib/kinema';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { useTimelineScale } from './useTimelineScale';
import { TimeRuler } from './TimeRuler';
import { LayerHeader } from './LayerHeader';
import { LayerTrack } from './LayerTrack';
import { EffectTrack } from './EffectTrack';
import { PlayheadCursor } from './PlayheadCursor';

const HEADER_WIDTH = 220;
const LAYER_ROW_HEIGHT = 28;
const EFFECT_ROW_HEIGHT = 24;

const EFFECT_COLORS: Record<string, string> = {
  x: '#ef4444',
  y: '#22c55e',
  opacity: '#8b5cf6',
  rotation: '#f59e0b',
  scaleX: '#06b6d4',
  scaleY: '#ec4899',
};

export function TimelinePanel() {
  const state = useEditorState();
  const editor = useEditor();
  const { staticMode } = useEditorActions();
  const comp = getCompositionProps(state);
  const scale = useTimelineScale(comp.duration || 10);
  const [expandedLayers, setExpandedLayers] = useState<Set<string>>(new Set());

  // Keyframe selection: "layerId:property:time" keys
  const [selectedKeyframes, setSelectedKeyframes] = useState<Set<string>>(new Set());
  const keyframeClipboardRef = useRef<{ property: string; keyframes: { time: number; value: any; easing: any }[] }[] | null>(null);
  // Group drag state: pixel dx while dragging selected keyframes
  const [groupDragDx, setGroupDragDx] = useState(0);
  const isDraggingKeyframesRef = useRef(false);

  const tracksRef = useRef<HTMLDivElement>(null);
  const headersRef = useRef<HTMLDivElement>(null);
  const rulerRef = useRef<HTMLDivElement>(null);

  const handleTracksScroll = useCallback(() => {
    const tracks = tracksRef.current;
    if (!tracks) return;
    if (headersRef.current) headersRef.current.scrollTop = tracks.scrollTop;
    if (rulerRef.current) rulerRef.current.scrollLeft = tracks.scrollLeft;
  }, []);

  // Forward wheel events from headers to tracks (headers is overflow:hidden)
  const handleHeadersWheel = useCallback((e: React.WheelEvent) => {
    if (tracksRef.current) {
      tracksRef.current.scrollTop += e.deltaY;
    }
  }, []);

  const handleSeek = useCallback(
    (time: number) => editor.seek(Math.max(0, Math.min(time, comp.duration))),
    [editor, comp.duration],
  );

  const toggleExpand = useCallback((layerId: string) => {
    setExpandedLayers((prev) => {
      const next = new Set(prev);
      if (next.has(layerId)) next.delete(layerId);
      else next.add(layerId);
      return next;
    });
  }, []);

  const handleKeyframeClick = useCallback((layerId: string, property: string, time: number, addToSelection: boolean) => {
    const key = `${layerId}:${property}:${time}`;
    setSelectedKeyframes((prev) => {
      if (addToSelection) {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }
      return new Set([key]);
    });
  }, []);

  // Track timeline focus + clear keyframe selection when clicking outside
  const timelinePanelRef = useRef<HTMLDivElement>(null);
  const [timelineFocused, setTimelineFocused] = useState(false);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const inside = timelinePanelRef.current?.contains(e.target as Node) ?? false;
      setTimelineFocused(inside);
      if (!inside && selectedKeyframes.size > 0) {
        setSelectedKeyframes(new Set());
      }
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [selectedKeyframes.size]);

  // Group drag handlers for multi-keyframe drag
  const handleGroupDragMove = useCallback((dx: number) => {
    isDraggingKeyframesRef.current = true;
    setGroupDragDx(dx);
  }, []);

  const handleGroupDragEnd = useCallback((dx: number) => {
    isDraggingKeyframesRef.current = false;
    setGroupDragDx(0);

    if (Math.abs(dx) < 1) return;

    // Move all selected keyframes by dx pixels
    const { frameRate } = getCompositionProps(state);
    const entries = Array.from(selectedKeyframes).map((key) => {
      const [layerId, property, timeStr] = key.split(':');
      const time = parseFloat(timeStr);
      return { layerId, property, time };
    });

    editor.batch(() => {
      // Remove all first, then add — avoids conflicts with overlapping times
      const toAdd: { layerId: string; property: string; time: number; value: any; easing: any }[] = [];

      for (const entry of entries) {
        const layer = state.layers[entry.layerId];
        if (!layer) continue;
        const effect = layer.effects[entry.property];
        if (!effect) continue;
        const kf = effect.keyframes.find((k: any) => Math.abs(k.time - entry.time) < 0.001);
        if (!kf) continue;

        const oldX = scale.timeToX(layer.startTime + kf.time);
        const newX = oldX + dx;
        const rawTime = Math.max(0, scale.xToTime(newX) - layer.startTime);
        const newTime = snapToFrame(rawTime, frameRate);

        editor.dispatchCommand(REMOVE_KEYFRAME, {
          layerId: entry.layerId,
          property: entry.property,
          time: entry.time,
        });

        toAdd.push({ layerId: entry.layerId, property: entry.property, time: newTime, value: kf.value, easing: kf.easing });
      }

      for (const item of toAdd) {
        editor.dispatchCommand(ADD_KEYFRAME, {
          layerId: item.layerId,
          property: item.property,
          keyframe: { time: item.time, value: item.value, easing: item.easing },
        });
      }

      // Update selection keys to new times
      const newKeys = new Set(toAdd.map((item) => `${item.layerId}:${item.property}:${item.time}`));
      setSelectedKeyframes(newKeys);
    });
  }, [selectedKeyframes, state, editor, scale]);

  const handleGroupDragCancel = useCallback(() => {
    isDraggingKeyframesRef.current = false;
    setGroupDragDx(0);
  }, []);

  // Arrow keys: nudge selected keyframes by ±1 frame, or scrub playhead if timeline focused
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // An arrow typed in a field moves the caret there, not the keyframes or the playhead.
      if (isTextEntryTarget(e.target)) return;

      const { frameRate } = getCompositionProps(state);
      const frameDuration = 1 / frameRate;
      const direction = e.key === 'ArrowRight' ? 1 : -1;

      if (selectedKeyframes.size > 0) {
        // Nudge selected keyframes by one frame
        e.preventDefault();
        e.stopImmediatePropagation();

        const entries = Array.from(selectedKeyframes).map((key) => {
          const [layerId, property, timeStr] = key.split(':');
          return { layerId, property, time: parseFloat(timeStr) };
        });

        editor.batch(() => {
          const toAdd: { layerId: string; property: string; time: number; value: any; easing: any }[] = [];

          for (const entry of entries) {
            const layer = state.layers[entry.layerId];
            if (!layer) continue;
            const effect = layer.effects[entry.property];
            if (!effect) continue;
            const kf = effect.keyframes.find((k: any) => Math.abs(k.time - entry.time) < 0.001);
            if (!kf) continue;

            const newTime = snapToFrame(Math.max(0, kf.time + direction * frameDuration), frameRate);
            editor.dispatchCommand(REMOVE_KEYFRAME, {
              layerId: entry.layerId,
              property: entry.property,
              time: entry.time,
            });
            toAdd.push({ layerId: entry.layerId, property: entry.property, time: newTime, value: kf.value, easing: kf.easing });
          }

          for (const item of toAdd) {
            editor.dispatchCommand(ADD_KEYFRAME, {
              layerId: item.layerId,
              property: item.property,
              keyframe: { time: item.time, value: item.value, easing: item.easing },
            });
          }

          setSelectedKeyframes(new Set(toAdd.map((item) => `${item.layerId}:${item.property}:${item.time}`)));
        });
        return;
      }

      if (timelineFocused) {
        // Scrub playhead by one frame
        e.preventDefault();
        e.stopImmediatePropagation();
        const newTime = Math.max(0, Math.min(comp.duration, state.playheadTime + direction * frameDuration));
        editor.seek(snapToFrame(newTime, frameRate));
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [selectedKeyframes, timelineFocused, state, editor, comp.duration]);

  // Keyframe copy/paste — uses capture phase to intercept before the editor's KeyMap handler
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (selectedKeyframes.size === 0) return;

      if (e.key === 'c') {
        // Copy selected keyframes, grouped by property
        const entries = Array.from(selectedKeyframes).map((key) => {
          const [layerId, property, timeStr] = key.split(':');
          const time = parseFloat(timeStr);
          const layer = state.layers[layerId];
          const effect = layer?.effects[property];
          const kf = effect?.keyframes.find((k: any) => Math.abs(k.time - time) < 0.001);
          return kf ? { property, time: kf.time, value: kf.value, easing: kf.easing } : null;
        }).filter(Boolean) as { property: string; time: number; value: any; easing: any }[];

        if (entries.length === 0) return;

        // Group by property
        const byProperty = new Map<string, typeof entries>();
        for (const entry of entries) {
          const group = byProperty.get(entry.property) ?? [];
          group.push(entry);
          byProperty.set(entry.property, group);
        }

        // Store each property group relative to the global earliest keyframe
        const globalMinTime = Math.min(...entries.map((e) => e.time));
        keyframeClipboardRef.current = Array.from(byProperty.entries()).map(([property, kfs]) => ({
          property,
          keyframes: kfs.map((kf) => ({ ...kf, time: kf.time - globalMinTime })),
        }));
        e.preventDefault();
        e.stopImmediatePropagation();
      }

      if (e.key === 'v' && keyframeClipboardRef.current) {
        // Paste to all selected layers at playhead
        const clips = keyframeClipboardRef.current;
        const playhead = state.playheadTime;
        const selectedIds = Array.from(state.selectedIds);
        if (selectedIds.length === 0) return;

        const { frameRate } = getCompositionProps(state);

        editor.batch(() => {
          for (const layerId of selectedIds) {
            const layer = state.layers[layerId];
            if (!layer) continue;

            const localPlayhead = playhead - layer.startTime;

            for (const clip of clips) {
              // Auto-create effect if it doesn't exist
              if (!layer.effects[clip.property]) {
                editor.dispatchCommand(ADD_EFFECT, {
                  layerId,
                  property: clip.property,
                  keyframes: [],
                });
              }

              // Add each keyframe at playhead-relative time
              for (const kf of clip.keyframes) {
                const newTime = snapToFrame(localPlayhead + kf.time, frameRate);
                editor.dispatchCommand(ADD_KEYFRAME, {
                  layerId,
                  property: clip.property,
                  keyframe: { time: newTime, value: kf.value, easing: kf.easing },
                });
              }
            }
          }
        });
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };
    // Capture phase ensures this fires before the editor's bubble-phase KeyMap handler
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [selectedKeyframes, state, editor]);

  // Reverse so visually-topmost layers (highest order) appear at top of timeline
  const layers = getRootLayers(state.layers, state.rootLayerId).reverse();
  const layerIds = layers.map((l) => l.id);

  // Build row data
  const rows: RowData[] = [];
  for (const layer of layers) {
    const effects = Object.values(layer.effects);
    const isExpanded = expandedLayers.has(layer.id);
    rows.push({ type: 'layer', layer, isExpanded, effectCount: effects.length });
    if (isExpanded) {
      for (const effect of effects) {
        rows.push({ type: 'effect', layer, effect });
      }
    }
  }

  const totalContentHeight = rows.reduce(
    (h, r) => h + (r.type === 'layer' ? LAYER_ROW_HEIGHT : EFFECT_ROW_HEIGHT),
    0,
  );

  // DnD sensors — require 5px movement to start drag (avoids conflicts with click)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleDragEnd = (event: { active: { id: string | number }; over: { id: string | number } | null }) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = layerIds.indexOf(String(active.id));
    const newIndex = layerIds.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    // Reorder: assign new order values to all layers
    const reordered = [...layerIds];
    reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, String(active.id));

    // Timeline is reversed (top = highest order), so assign orders in reverse
    const maxOrder = reordered.length - 1;
    editor.batch(() => {
      reordered.forEach((id, i) => {
        const newOrder = maxOrder - i;
        if (state.layers[id].order !== newOrder) {
          editor.dispatchCommand(UPDATE_PROPERTY, { layerId: id, property: 'order', value: newOrder });
        }
      });
    });
  };

  // ─── Static mode: layer list only ───────────────────────
  if (staticMode) {
    return (
      <div ref={timelinePanelRef} style={{
        height: '100%', background: '#ffffff', borderTop: '1px solid #e2e8f0',
        fontFamily: 'system-ui, -apple-system, sans-serif', overflow: 'hidden', userSelect: 'none',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{
          background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
          padding: '6px 8px', fontSize: 11, fontWeight: 600, color: '#475569',
          flexShrink: 0,
        }}>
          Layers
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
            <SortableContext items={layerIds} strategy={verticalListSortingStrategy}>
              {layers.map((layer) => (
                <SortableLayerHeader
                  key={`h-${layer.id}`}
                  layer={layer}
                  isSelected={state.selectedIds.has(layer.id)}
                  isExpanded={false}
                  onToggleExpand={() => {}}
                  effectCount={0}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </div>
    );
  }

  // ─── Video mode: full timeline ────────────────────────────
  return (
    <div
      ref={timelinePanelRef}
      style={{
        display: 'grid',
        gridTemplateColumns: `${HEADER_WIDTH}px 1fr`,
        gridTemplateRows: '28px 1fr',
        height: '100%',
        background: '#ffffff',
        borderTop: '1px solid #e2e8f0',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        overflow: 'hidden',
        userSelect: 'none',
      }}
    >
      {/* Top-left corner — timecode */}
      <div
        style={{
          background: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          borderRight: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingLeft: 8,
          paddingRight: 8,
          fontSize: 10,
          fontWeight: 600,
          color: '#1e293b',
        }}
      >
        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {formatTimecode(state.playheadTime, comp.frameRate)}
        </span>
        <span style={{ color: '#94a3b8', fontSize: 9, fontWeight: 400 }}>
          {comp.frameRate}fps
        </span>
      </div>

      {/* Time ruler */}
      <div
        ref={rulerRef}
        style={{
          overflow: 'hidden',
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <TimeRuler scale={scale} playheadTime={state.playheadTime} onSeek={handleSeek} />
      </div>

      {/* Layer headers — with DnD sortable */}
      <div
        ref={headersRef}
        onWheel={handleHeadersWheel}
        style={{
          overflow: 'hidden',
          borderRight: '1px solid #e2e8f0',
          background: '#ffffff',
        }}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={layerIds} strategy={verticalListSortingStrategy}>
            {rows.map((row) =>
              row.type === 'layer' ? (
                <SortableLayerHeader
                  key={`h-${row.layer.id}`}
                  layer={row.layer}
                  isSelected={state.selectedIds.has(row.layer.id)}
                  isExpanded={row.isExpanded}
                  onToggleExpand={() => toggleExpand(row.layer.id)}
                  effectCount={row.effectCount}
                />
              ) : (
                <EffectHeaderRow
                  key={`eh-${row.layer.id}-${row.effect!.id}`}
                  effect={row.effect!}
                  onDisable={() =>
                    editor.dispatchCommand(REMOVE_EFFECT, { layerId: row.layer.id, property: row.effect!.property })
                  }
                />
              ),
            )}
          </SortableContext>
        </DndContext>
      </div>

      {/* Track area */}
      <div
        ref={tracksRef}
        onScroll={handleTracksScroll}
        style={{
          overflow: 'auto',
          position: 'relative',
          background: '#fafbfc',
        }}
      >
        <TimeGridLines scale={scale} totalHeight={totalContentHeight} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          {rows.map((row) =>
            row.type === 'layer' ? (
              <LayerTrack
                key={`t-${row.layer.id}`}
                layer={row.layer}
                scale={scale}
                isSelected={state.selectedIds.has(row.layer.id)}
              />
            ) : (
              <EffectTrack
                key={`et-${row.layer.id}-${row.effect!.id}`}
                effect={row.effect!}
                scale={scale}
                layerStartTime={row.layer.startTime}
                layerId={row.layer.id}
                color={EFFECT_COLORS[row.effect!.property] ?? '#94a3b8'}
                selectedKeyframes={selectedKeyframes}
                onKeyframeClick={handleKeyframeClick}
                onKeyframeDragMove={handleGroupDragMove}
                onKeyframeDragEnd={handleGroupDragEnd}
                onKeyframeDragCancel={handleGroupDragCancel}
                groupDragDx={groupDragDx}
              />
            ),
          )}
        </div>

        <PlayheadCursor
          time={state.playheadTime}
          scale={scale}
          height={totalContentHeight}
        />
      </div>
    </div>
  );
}

/** Inputs where ArrowLeft/Right move a caret. A checkbox or a range keeps the timeline's arrows. */
const TEXT_INPUT_TYPES = new Set(['text', 'search', 'url', 'email', 'tel', 'password', 'number']);

function isTextEntryTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  if (el.tagName === 'TEXTAREA' || el.isContentEditable) return true;
  return el.tagName === 'INPUT' && TEXT_INPUT_TYPES.has((el as HTMLInputElement).type);
}

// ─── Sortable layer header wrapper ────────────────────────

function SortableLayerHeader(props: {
  layer: BaseLayerData;
  isSelected: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  effectCount: number;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.layer.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative' as const,
    zIndex: isDragging ? 10 : 0,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <LayerHeader
        {...props}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
}

// ─── Row data types ───────────────────────────────────────

type RowData =
  | { type: 'layer'; layer: BaseLayerData; isExpanded: boolean; effectCount: number }
  | { type: 'effect'; layer: BaseLayerData; effect: Effect };

// ─── Effect header sub-row ────────────────────────────────

function EffectHeaderRow({ effect, onDisable }: { effect: Effect; onDisable: () => void }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        height: EFFECT_ROW_HEIGHT,
        paddingLeft: 36,
        gap: 6,
        background: '#f8fafc',
        borderBottom: '1px solid #f1f5f9',
        fontSize: 11,
        color: '#64748b',
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: EFFECT_COLORS[effect.property] ?? '#94a3b8',
          flexShrink: 0,
        }}
      />
      <button
        onClick={onDisable}
        title="Disable keyframing for this property"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          width: 14,
          height: 14,
          flexShrink: 0,
          color: '#2563eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg width="12" height="12" viewBox="0 0 14 14" fill="currentColor">
          <circle cx="7" cy="8" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path d="M7 5v3.5l2 1.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          <path d="M7 1v2" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          <path d="M5 1.5h4" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      </button>
      {effect.property}
      <span style={{ color: '#cbd5e1', marginLeft: 'auto', paddingRight: 8, fontSize: 10 }}>
        {effect.keyframes.length} kf
      </span>
    </div>
  );
}

// ─── Time grid lines ──────────────────────────────────────

function TimeGridLines({
  scale,
  totalHeight,
}: {
  scale: ReturnType<typeof useTimelineScale>;
  totalHeight: number;
}) {
  const { duration, pixelsPerSecond, totalWidth, timeToX } = scale;
  const interval = pixelsPerSecond >= 80 ? 1 : pixelsPerSecond >= 30 ? 2 : 5;

  const lines: number[] = [];
  for (let t = 0; t <= duration; t += interval) {
    lines.push(t);
  }

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: totalWidth,
        height: totalHeight,
        pointerEvents: 'none',
        zIndex: 0,
      }}
    >
      {lines.map((t) => (
        <div
          key={t}
          style={{
            position: 'absolute',
            left: timeToX(t),
            top: 0,
            width: 1,
            height: '100%',
            background: '#f1f5f9',
          }}
        />
      ))}
    </div>
  );
}
